import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { approveSignup, rejectSignup } from "../../api/billing";
import { NeuCard } from "../../components/neu/Neu";
import { usePerms } from "../../hooks/usePerms";
import { SignupSearch } from "./SignupSearch";
import { SignupRow } from "./SignupRow";

export const SignupRequests = ({ requests, reload }) => {
  const { t } = useTranslation();
  const canSupport = usePerms().can("support");
  const [reasons, setReasons] = useState({});
  const [busy, setBusy] = useState("");
  const [lastSetup, setLastSetup] = useState(null);
  if (!canSupport) return null;

  const act = async (id, fn, message) => {
    setBusy(id);
    try {
      const res = await fn();
      if (res?.setup_url) setLastSetup({ email: res.admin_email, url: res.setup_url });
      toast.success(message); reload();
    } catch (err) {
      const d = err.response?.data?.detail;
      const code = typeof d === "string" ? d : d?.code;
      if (code && t(`errors.${code}`, { defaultValue: "" })) {
        const extra = d?.company_name ? ` (${d.company_name})` : d?.email ? ` — ${d.email}` : "";
        toast.error(t(`errors.${code}`) + extra);
      } else { toast.error(t("errors.generic")); }
    } finally { setBusy(""); }
  };

  return (
    <NeuCard data-testid="signup-requests">
      <p className="label-xs">{t("platformExtra.requests")}</p>
      <div className="mt-3">
        <SignupSearch />
      </div>
      {lastSetup ? (
        <div className="mt-4 rounded-2xl border border-primary/40 p-4 shadow-neu-in" data-testid="signup-setup-link">
          <p className="text-xs text-muted-foreground">{t("platformExtra.setupLinkNote")}</p>
          <p className="mt-1 text-xs font-semibold text-primary">{lastSetup.email}</p>
          <code className="mt-2 block break-all font-mono text-[11px] text-primary">{lastSetup.url}</code>
        </div>
      ) : null}
      {requests.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground" data-testid="signup-requests-empty">
          {t("platformExtra.requestsEmpty")}
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {requests.map((r) => (
            <SignupRow key={r.id} r={r} busy={busy === r.id}
                       reason={reasons[r.id] || ""}
                       setReason={(v) => setReasons({ ...reasons, [r.id]: v })}
                       onApprove={() => act(r.id, () => approveSignup(r.id), t("platformExtra.approved"))}
                       onReject={(reason) => act(r.id, () => rejectSignup(r.id, reason), t("platformExtra.rejectedMsg"))} />
          ))}
        </ul>
      )}
    </NeuCard>
  );
};
